import type {
  AuthResponse,
  UserPublic,
  ProjectResponse,
  TrackResponse,
  InviteResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateTrackRequest,
  UpdateTrackRequest,
  TrackPermissionEntry,
  TrackPermissionLevel,
  CommentResponse,
  CreateCommentRequest,
  UpdateCommentRequest,
  UserSearchResult,
  FriendResponse,
  FriendRequestResponse,
} from '@smplecut/shared';

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public serverMessage?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new ApiError('Network error — please check your connection', 0);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body.error || `Request failed (${res.status})`;
    throw new ApiError(msg, res.status, body.error);
  }

  return res.json();
}

export const api = {
  // Auth
  register: (data: { email: string; password: string; name: string }) =>
    request<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data: { email: string; password: string }) =>
    request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  googleSignIn: (credential: string) =>
    request<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    }),
  me: () => request<{ user: UserPublic }>('/auth/me'),
  sendVerificationEmail: () =>
    request<{ ok: true; alreadyVerified?: boolean }>('/auth/verify-email/send', { method: 'POST' }),
  verifyEmail: (token: string) =>
    request<{ ok: true }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  requestPasswordReset: (email: string) =>
    request<{ ok: true }>('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (token: string, password: string) =>
    request<{ ok: true }>('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  deleteAccount: (password?: string) =>
    request<{ ok: true }>('/auth/me', {
      method: 'DELETE',
      body: JSON.stringify({ password: password ?? '' }),
    }),
  requestEmailChange: (newEmail: string, password?: string) =>
    request<{ ok: true }>('/auth/email-change/request', {
      method: 'POST',
      body: JSON.stringify({ newEmail, password: password ?? '' }),
    }),
  confirmEmailChange: (token: string) =>
    request<{ ok: true }>('/auth/email-change/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  listSessions: () =>
    request<{
      sessions: {
        id: string;
        userAgent: string | null;
        ip: string | null;
        createdAt: string;
        current: boolean;
      }[];
    }>('/auth/sessions'),
  revokeSession: (id: string) =>
    request<{ ok: true }>(`/auth/sessions/${id}`, { method: 'DELETE' }),
  revokeOtherSessions: () =>
    request<{ ok: true; revoked: number }>('/auth/sessions/revoke-others', { method: 'POST' }),

  // Projects
  listProjects: () => request<{ projects: ProjectResponse[] }>('/projects'),
  createProject: (data: CreateProjectRequest) =>
    request<ProjectResponse>('/projects', { method: 'POST', body: JSON.stringify(data) }),
  getProject: (id: string) => request<ProjectResponse>(`/projects/${id}`),
  updateProject: (id: string, data: UpdateProjectRequest) =>
    request<ProjectResponse>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProject: (id: string) =>
    request<{ success: boolean }>(`/projects/${id}`, { method: 'DELETE' }),
  duplicateProject: (id: string) =>
    request<ProjectResponse>(`/projects/${id}/duplicate`, { method: 'POST' }),

  // Project members
  getProjectMembers: (projectId: string) =>
    request<{ members: { id: string; userId: string; name: string; email: string; role: string; createdAt: string }[] }>(
      `/projects/${projectId}/members`,
    ),
  removeProjectMember: (projectId: string, userId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' }),

  // Invites
  createInvite: (projectId: string, email: string) =>
    request<InviteResponse>(`/projects/${projectId}/invites`, { method: 'POST', body: JSON.stringify({ email }) }),
  listInvites: () => request<{ invites: (InviteResponse & { projectTitle: string })[] }>('/invites'),
  acceptInvite: (token: string) =>
    request<{ message: string }>(`/invites/${token}/accept`, { method: 'POST' }),
  declineInvite: (token: string) =>
    request<{ success: boolean }>(`/invites/${token}/decline`, { method: 'POST' }),

  // Sequencer patterns
  listPatterns: (projectId: string) =>
    request<{ patterns: { id: string; projectId: string; title: string; patternJson: any; createdAt: string; updatedAt: string }[] }>(
      `/projects/${projectId}/patterns`,
    ),
  createPattern: (projectId: string, title: string, patternJson: any) =>
    request<{ id: string; projectId: string; title: string; patternJson: any; createdAt: string; updatedAt: string }>(
      `/projects/${projectId}/patterns`, { method: 'POST', body: JSON.stringify({ title, patternJson }) },
    ),
  updatePattern: (projectId: string, patternId: string, data: { title?: string; patternJson?: any }) =>
    request<{ id: string; projectId: string; title: string; patternJson: any; createdAt: string; updatedAt: string }>(
      `/projects/${projectId}/patterns/${patternId}`, { method: 'PATCH', body: JSON.stringify(data) },
    ),
  deletePattern: (projectId: string, patternId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/patterns/${patternId}`, { method: 'DELETE' }),

  // Presence
  sendHeartbeat: (projectId: string) =>
    request<{ ok: boolean }>(`/projects/${projectId}/heartbeat`, { method: 'POST' }),
  getPresence: (projectId: string) =>
    request<{ users: { userId: string; name: string; email: string }[] }>(`/projects/${projectId}/presence`),

  // Tracks
  listTracks: (projectId: string) =>
    request<{ tracks: TrackResponse[] }>(`/projects/${projectId}/tracks`),
  createTrack: (projectId: string, data: CreateTrackRequest) =>
    request<TrackResponse>(`/projects/${projectId}/tracks`, { method: 'POST', body: JSON.stringify(data) }),
  getTrack: (projectId: string, trackId: string) =>
    request<TrackResponse>(`/projects/${projectId}/tracks/${trackId}`),
  updateTrack: (projectId: string, trackId: string, data: UpdateTrackRequest & { expectedUpdatedAt?: string | null }) =>
    request<TrackResponse>(`/projects/${projectId}/tracks/${trackId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTrack: (projectId: string, trackId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/tracks/${trackId}`, { method: 'DELETE' }),

  // Track versions
  listTrackVersions: (projectId: string, trackId: string) =>
    request<{ versions: import('@smplecut/shared').TrackVersionResponse[] }>(`/projects/${projectId}/tracks/${trackId}/versions`),
  getTrackVersion: (projectId: string, trackId: string, versionId: string) =>
    request<import('@smplecut/shared').TrackVersionDetail>(`/projects/${projectId}/tracks/${trackId}/versions/${versionId}`),
  restoreTrackVersion: (projectId: string, trackId: string, versionId: string) =>
    request<TrackResponse>(`/projects/${projectId}/tracks/${trackId}/versions/${versionId}/restore`, { method: 'POST' }),
  createTrackSnapshot: (projectId: string, trackId: string, label: string) =>
    request<import('@smplecut/shared').TrackVersionResponse>(`/projects/${projectId}/tracks/${trackId}/snapshots`, {
      method: 'POST', body: JSON.stringify({ label }),
    }),

  // Track permissions
  getTrackPermissions: (projectId: string, trackId: string) =>
    request<{ permissions: TrackPermissionEntry[] }>(`/projects/${projectId}/tracks/${trackId}/permissions`),
  setTrackPermission: (projectId: string, trackId: string, userId: string, permission: TrackPermissionLevel) =>
    request<{ success: boolean }>(`/projects/${projectId}/tracks/${trackId}/permissions/${userId}`, {
      method: 'PUT', body: JSON.stringify({ permission }),
    }),
  removeTrackPermission: (projectId: string, trackId: string, userId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/tracks/${trackId}/permissions/${userId}`, { method: 'DELETE' }),

  // Chat history
  getChatHistory: (projectId: string, before?: number, limit = 50) =>
    request<{ messages: import('@smplecut/shared').ChatMessage[] }>(
      `/projects/${projectId}/chat?${before ? `before=${before}&` : ''}limit=${limit}`,
    ),

  // Comments
  listComments: (projectId: string, params?: { anchorType?: string; trackId?: string; patternId?: string; resolved?: string }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]).toString() : '';
    return request<{ comments: CommentResponse[] }>(`/projects/${projectId}/comments${qs}`);
  },
  listCommentReplies: (projectId: string, commentId: string) =>
    request<{ replies: CommentResponse[] }>(`/projects/${projectId}/comments/${commentId}/replies`),
  createComment: (projectId: string, data: CreateCommentRequest) =>
    request<CommentResponse>(`/projects/${projectId}/comments`, { method: 'POST', body: JSON.stringify(data) }),
  updateComment: (projectId: string, commentId: string, data: UpdateCommentRequest) =>
    request<CommentResponse>(`/projects/${projectId}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteComment: (projectId: string, commentId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/comments/${commentId}`, { method: 'DELETE' }),

  // Friends
  searchUsers: (q: string) =>
    request<{ results: UserSearchResult[] }>(`/friends/search?q=${encodeURIComponent(q)}`),
  listFriends: () => request<{ friends: FriendResponse[] }>('/friends'),
  listFriendRequests: () =>
    request<{ requests: FriendRequestResponse[] }>('/friends/requests'),
  sendFriendRequest: (userId: string) =>
    request<{ friendshipId: string; status: 'pending' | 'accepted' }>(`/friends/requests`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  acceptFriendRequest: (friendshipId: string) =>
    request<{ success: boolean }>(`/friends/requests/${friendshipId}/accept`, { method: 'POST' }),
  declineFriendRequest: (friendshipId: string) =>
    request<{ success: boolean }>(`/friends/requests/${friendshipId}/decline`, { method: 'POST' }),
  removeFriend: (userId: string) =>
    request<{ success: boolean }>(`/friends/${userId}`, { method: 'DELETE' }),
};
